import React, { useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Check, Download, Link as LinkIcon, Users, Eye, 
  Trash2, Volume2, VolumeX, Info, RotateCw, Heart 
} from 'lucide-react'
import { toast } from 'sonner'
import { 
  WEATHERS, RESIDUE_TAGS, 
  weave, makeSecret, makeReceipt, canDeposit 
} from '../worker/shared'
import type { WeatherId, ResidueTag, Deposit, ReplaySnapshot } from '../worker/shared'

const API_BASE = (import.meta as any).env?.VITE_API_BASE || ''

// Persistent anonymous session (only the secret ever leaves as a hash)
const SECRET_KEY = 'remainder_secret_v1'
function getOrCreateSecret(): string {
  let s = localStorage.getItem(SECRET_KEY)
  if (!s) {
    s = makeSecret()
    localStorage.setItem(SECRET_KEY, s)
  }
  return s
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms))

// Subtle mechanical / paper / civic sounds (Web Audio, toggleable, respectful)
let audioCtx: AudioContext | null = null
let masterGain: GainNode | null = null
let isMuted = localStorage.getItem('remainder_muted') === '1'

function ensureAudio() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    masterGain = audioCtx.createGain()
    masterGain.gain.value = isMuted ? 0.0001 : 0.22
    masterGain.connect(audioCtx.destination)
  }
  return { ctx: audioCtx!, gain: masterGain! }
}

function playThunk() {
  try {
    const { ctx, gain } = ensureAudio()
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    const f = ctx.createBiquadFilter()
    o.type = 'sawtooth'
    o.frequency.value = 68
    f.type = 'lowpass'
    f.frequency.value = 420
    g.gain.value = 0.9
    const g2 = ctx.createGain()
    g2.gain.value = 0.0001
    const t = ctx.currentTime
    g2.gain.linearRampToValueAtTime(0.6, t + 0.012)
    g2.gain.linearRampToValueAtTime(0.0001, t + 0.72)
    const n = ctx.createBufferSource()
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate)
    const data = buf.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() - 0.5) * 0.6
    n.buffer = buf
    const ng = ctx.createGain()
    ng.gain.value = 0.035
    n.connect(ng).connect(gain)
    o.connect(f).connect(g).connect(g2).connect(gain)
    o.start(t)
    n.start(t)
    o.stop(t + 0.9)
    n.stop(t + 0.7)
  } catch {}
}

function playPaper() {
  try {
    const { ctx, gain } = ensureAudio()
    const n = ctx.createBufferSource()
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.55, ctx.sampleRate)
    const d = buf.getChannelData(0)
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() - 0.5) * (1 - i / d.length)
    n.buffer = buf
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 1250
    bp.Q.value = 2.8
    const g = ctx.createGain()
    g.gain.value = 0.0001
    const t = ctx.currentTime
    g.gain.linearRampToValueAtTime(0.38, t + 0.03)
    g.gain.linearRampToValueAtTime(0.0001, t + 0.58)
    n.connect(bp).connect(g).connect(gain)
    n.start(t)
    n.stop(t + 0.7)
  } catch {}
}

function playChime(bright = false) {
  try {
    const { ctx, gain } = ensureAudio()
    const o = ctx.createOscillator()
    o.type = 'sine'
    o.frequency.value = bright ? 1180 : 740
    const g = ctx.createGain()
    g.gain.value = 0.0001
    const t = ctx.currentTime
    g.gain.linearRampToValueAtTime(0.28, t + 0.012)
    g.gain.linearRampToValueAtTime(0.0001, t + 1.6)
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = bright ? 2400 : 1600
    o.connect(lp).connect(g).connect(gain)
    o.start(t)
    o.stop(t + 1.9)
  } catch {}
}

function toggleMute() {
  isMuted = !isMuted
  localStorage.setItem('remainder_muted', isMuted ? '1' : '0')
  if (masterGain) masterGain.gain.value = isMuted ? 0.0001 : 0.22
  return !isMuted
}

// The full functional app
export default function Remainder() {
  const secret = useRef(getOrCreateSecret())
  const [muted, setMuted] = useState(isMuted)

  // Global navigation
  const [view, setView] = useState<'arrival' | 'arcade' | 'replay' | 'ledger'>('arrival')
  const [showAbout, setShowAbout] = useState(false)

  // Arcade state (the overconfidence machines)
  const [optText, setOptText] = useState('')
  const [overconfidence, setOverconfidence] = useState(78)
  const [redactions, setRedactions] = useState<number[]>([]) // indices of cut sentences in the scaffold
  const [weather, setWeather] = useState<WeatherId>('low_static')
  const [weatherNote, setWeatherNote] = useState('')
  const [residue, setResidue] = useState<ResidueTag[]>(['hollowness'])
  const [intensity, setIntensity] = useState(3)
  const [agency, setAgency] = useState(31)
  const [unresolved, setUnresolved] = useState('')
  const [isDropping, setIsDropping] = useState(false)
  const [lastDepositTs, setLastDepositTs] = useState<number | null>(null)
  const [myReceipt, setMyReceipt] = useState<string | null>(null)

  // Replay + collective
  const [currentSnapshot, setCurrentSnapshot] = useState<ReplaySnapshot | null>(null)
  const [_isLoadingReplay, setIsLoadingReplay] = useState(false)
  const [hallCount, setHallCount] = useState(3)
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null)
  const [resonanceText, setResonanceText] = useState('')
  const [isWeaving, setIsWeaving] = useState(false)

  // Private ledger (only visible to this secret)
  const [myDeposits, setMyDeposits] = useState<any[]>([])
  const [isErasing, setIsErasing] = useState<string | null>(null)

  // Local simulation (so the experience is complete and beautiful even without worker)
  const [localDeposits, setLocalDeposits] = useState<Deposit[]>([])
  const [localResonances, setLocalResonances] = useState<any[]>([])

  const canDrop = optText.trim().length > 8 && canDeposit(lastDepositTs)

  // Derived current "preview" weave using pure shared function (works offline + optimistic)
  const previewWeave = useMemo(() => {
    const base: Deposit[] = localDeposits.length > 0 ? localDeposits : seedDemoDeposits()
    const withMine = optText.trim().length > 8 ? [...base, {
      id: 'preview',
      created_at: Math.floor(Date.now() / 1000),
      optimization_text: optText,
      overconfidence,
      weather_id: weather,
      weather_note: weatherNote || null,
      residue_tags: residue,
      residue_intensity: intensity,
      agency_rating: agency,
      unresolved_text: unresolved.trim() || null,
    } as any] : base
    return weave(withMine as any, localResonances)
  }, [localDeposits, optText, overconfidence, weather, weatherNote, residue, intensity, agency, unresolved, localResonances])

  // Load current replay on mount + when we enter replay view
  useEffect(() => {
    if (view === 'replay') loadCurrentReplay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  // Quiet presence pulse (simulated + real)
  useEffect(() => {
    const id = setInterval(() => {
      setHallCount(n => Math.max(1, Math.min(11, n + (Math.random() > 0.7 ? 1 : -1))))
    }, 14000)
    return () => clearInterval(id)
  }, [])

  // Keyboard poetry
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === 'r' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setView('replay')
        loadCurrentReplay()
      }
      if (e.key.toLowerCase() === 'l' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setView('ledger')
        loadMyLedger()
      }
      if (e.key === 'Escape') {
        setSelectedCluster(null)
        setShowAbout(false)
      }
      if (e.key.toLowerCase() === '?' && view === 'arrival') {
        setShowAbout(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [view])

  async function api(path: string, init?: RequestInit) {
    const res = await fetch(`${API_BASE}/api${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    return res.json()
  }

  // === DEPOSIT RITUAL ===
  async function dropRemainder() {
    if (!canDrop) return
    setIsDropping(true)
    playThunk()

    const receipt = makeReceipt()
    const depositPayload = {
      optimization_text: optText.trim(),
      overconfidence,
      redaction_json: JSON.stringify({ cuts: redactions }),
      weather_id: weather,
      weather_note: weatherNote.trim() || null,
      residue_tags: residue,
      residue_intensity: intensity,
      agency_rating: agency,
      unresolved_text: unresolved.trim() || null,
      receipt,
      secret: secret.current,
    }

    try {
      const res = await api('/deposit', { method: 'POST', body: JSON.stringify(depositPayload) })
      setMyReceipt(res.receipt || receipt)
    } catch (e: any) {
      // Local simulation path — the experience remains complete and meaningful
      const localDep: Deposit = {
        id: 'local_' + Date.now(),
        created_at: Math.floor(Date.now() / 1000),
        optimization_text: depositPayload.optimization_text,
        overconfidence: depositPayload.overconfidence,
        weather_id: depositPayload.weather_id as any,
        weather_note: depositPayload.weather_note,
        residue_tags: depositPayload.residue_tags as any,
        residue_intensity: depositPayload.residue_intensity,
        agency_rating: depositPayload.agency_rating,
        unresolved_text: depositPayload.unresolved_text,
      }
      setLocalDeposits(prev => [localDep, ...prev].slice(0, 48))
      setMyReceipt(receipt)
      toast.info('Worker unreachable — your remainder is held locally for this session. The weave still works.')
    }

    // Always update local preview + my ledger view
    const localDep: Deposit = {
      id: 'local_' + Date.now(),
      created_at: Math.floor(Date.now() / 1000),
      optimization_text: depositPayload.optimization_text,
      overconfidence: depositPayload.overconfidence,
      weather_id: depositPayload.weather_id as any,
      weather_note: depositPayload.weather_note,
      residue_tags: depositPayload.residue_tags as any,
      residue_intensity: depositPayload.residue_intensity,
      agency_rating: depositPayload.agency_rating,
      unresolved_text: depositPayload.unresolved_text,
    }
    setLocalDeposits(prev => [localDep, ...prev].slice(0, 48))
    setLastDepositTs(Date.now())

    playChime(true)

    // Clear the machines (ceremonial reset)
    setOptText('')
    setOverconfidence(78)
    setRedactions([])
    setWeather('low_static')
    setWeatherNote('')
    setResidue(['hollowness'])
    setIntensity(3)
    setAgency(31)
    setUnresolved('')

    setIsDropping(false)

    toast.success('Remainder placed. The loom will incorporate it on the next pass.', { duration: 4200 })

    // Move the visitor gently into the replay hall — the point of the instrument
    await sleep(420)
    setView('replay')
    await loadCurrentReplay(true)
  }

  // === REPLAY HALL ===
  async function loadCurrentReplay(forceWeave = false) {
    setIsLoadingReplay(true)
    try {
      const data = await api(`/replay/current?secret=${encodeURIComponent(secret.current)}`)
      if (data?.snapshot) {
        setCurrentSnapshot(data.snapshot)
      } else {
        // fall back to pure local weave
        const snap = buildSnapshotFromPreview()
        setCurrentSnapshot(snap)
      }
    } catch {
      const snap = buildSnapshotFromPreview()
      setCurrentSnapshot(snap)
    }
    setIsLoadingReplay(false)

    if (forceWeave) {
      // Friendly nudge: if the user just deposited, offer an explicit re-weave
      setTimeout(() => {
        if (localDeposits.length > 0) toast('You can request a fresh weave below if the pattern should already include your thread.')
      }, 1600)
    }
  }

  function buildSnapshotFromPreview(): ReplaySnapshot {
    const w = previewWeave
    return {
      id: 'local-preview',
      woven_at: Math.floor(Date.now() / 1000),
      summary_lines: w.summary_lines,
      stats: w.stats,
      weave: w.weave,
    } as any
  }

  async function requestReweave() {
    setIsWeaving(true)
    playThunk()
    try {
      await api('/reweave', { method: 'POST', body: JSON.stringify({ secret: secret.current }) })
      await sleep(380)
      await loadCurrentReplay()
      toast.success('The loom has passed. The replay is fresh.')
    } catch {
      // Pure client re-weave using everything we have (including local + any resonances)
      const base = localDeposits.length ? localDeposits : seedDemoDeposits()
      const w = weave(base as any, localResonances)
      const snap: ReplaySnapshot = {
        id: 'client-weave-' + Date.now(),
        woven_at: Math.floor(Date.now() / 1000),
        summary_lines: w.summary_lines,
        stats: w.stats,
        weave: w.weave,
      } as any
      setCurrentSnapshot(snap)
      toast('Re-wove locally with everything in this browser session.')
    }
    setIsWeaving(false)
  }

  async function addResonance() {
    if (!selectedCluster || !resonanceText.trim()) return
    const text = resonanceText.trim().slice(0, 118)
    playPaper()

    try {
      await api('/resonance', {
        method: 'POST',
        body: JSON.stringify({
          cluster_key: selectedCluster,
          resonance_text: text,
          secret: secret.current,
        }),
      })
    } catch {
      setLocalResonances(prev => [...prev, {
        id: 'lr_' + Date.now(),
        cluster_key: selectedCluster,
        resonance_text: text,
        created_at: Math.floor(Date.now() / 1000),
      }])
    }

    setResonanceText('')
    setSelectedCluster(null)
    toast('Resonance added. It will color the next weave.')

    // Immediately show the effect in the current view
    await loadCurrentReplay()
  }

  // === PRIVATE LEDGER + ERASURE ===
  async function loadMyLedger() {
    try {
      const data = await api(`/ledger/mine?secret=${encodeURIComponent(secret.current)}`)
      setMyDeposits(data.deposits || [])
    } catch {
      // Local only
      const mapped = localDeposits.slice(0, 12).map((d, _i) => ({
        id: d.id,
        created_at: d.created_at,
        summary: `${d.optimization_text.slice(0, 64)}${d.optimization_text.length > 64 ? '…' : ''} — agency ${d.agency_rating}`,
      }))
      setMyDeposits(mapped)
    }
  }

  async function eraseDeposit(depositId: string, receipt?: string) {
    setIsErasing(depositId)
    try {
      await api('/erase', {
        method: 'POST',
        body: JSON.stringify({ deposit_id: depositId, receipt: receipt || myReceipt, secret: secret.current }),
      })
      toast.success('That remainder has been removed from future weaves.')
    } catch {
      setLocalDeposits(prev => prev.filter(d => d.id !== depositId))
      toast('Erased from this browser session.')
    }
    setIsErasing(null)
    await loadMyLedger()
    await loadCurrentReplay()
  }

  // === WITNESS CARD (beautiful export) ===
  async function exportWitnessCard() {
    const snap = currentSnapshot || buildSnapshotFromPreview()
    const canvas = document.createElement('canvas')
    canvas.width = 1080
    canvas.height = 720
    const ctx = canvas.getContext('2d', { alpha: true })!

    // Paper background
    ctx.fillStyle = '#f6f2e9'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    // Subtle border
    ctx.strokeStyle = '#8a6f47'
    ctx.lineWidth = 3
    ctx.strokeRect(28, 28, canvas.width - 56, canvas.height - 56)

    // Title
    ctx.fillStyle = '#1f1912'
    ctx.font = '600 42px Georgia, serif'
    ctx.fillText('remainder', 72, 92)

    ctx.font = '400 18px ui-monospace, monospace'
    ctx.fillStyle = '#6b5f4f'
    ctx.fillText('a cooperation replay • ' + new Date(snap.woven_at * 1000).toISOString().slice(0, 10), 74, 120)

    // Summary lines
    ctx.font = '400 21px Georgia, serif'
    ctx.fillStyle = '#2c2520'
    let y = 178
    for (const line of snap.summary_lines.slice(0, 4)) {
      const words = line.split(' ')
      let row = ''
      for (const w of words) {
        const test = row + w + ' '
        if (ctx.measureText(test).width > 860) {
          ctx.fillText(row.trim(), 74, y)
          y += 32
          row = w + ' '
        } else row = test
      }
      ctx.fillText(row.trim(), 74, y)
      y += 38
    }

    // Stats strip
    y += 18
    ctx.font = '500 15px ui-monospace, monospace'
    ctx.fillStyle = '#8b5e3c'
    ctx.fillText(`DEPOSITS ${snap.stats.depositCount}   •   MEAN AGENCY ${snap.stats.meanAgency}   •   KNOTS ${snap.stats.knotCount}   •   HELD QUESTIONS ${snap.stats.heldQuestions}`, 74, y)

    // Simple thread drawing (same spirit as the live canvas)
    y += 52
    ctx.strokeStyle = '#5c4630'
    ctx.lineWidth = 1.8
    for (let i = 0; i < Math.min(9, snap.weave.threads.length); i++) {
      ctx.beginPath()
      const sx = 110 + i * 78
      ctx.moveTo(sx, y + 40)
      ctx.quadraticCurveTo(sx + 38, y - 8 + (i % 3) * 11, sx + 76, y + 52 + (i % 2) * 6)
      ctx.stroke()
    }

    // Footer civic notice + links
    ctx.font = '400 13px ui-monospace, monospace'
    ctx.fillStyle = '#6b5f4f'
    ctx.fillText('unresolved questions are permitted to remain unresolved', 74, 640)
    ctx.fillText('https://www.linkedin.com/in/mindful-abhay/  •  https://buymeacoffee.com/abhaypratapsingh', 74, 666)

    // Download
    const url = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = `remainder-replay-${snap.id.slice(0, 8)}.png`
    document.body.appendChild(a)
    a.click()
    a.remove()
    playChime()
    toast.success('Witness card exported. Carry it carefully.')
  }

  // === REDACTION TABLE (real interaction) ===
  const redactionScaffold = [
    "I said I would circle back after lunch.",
    "The message sat unread for most of the afternoon.",
    "I told myself it could wait until the numbers were cleaner.",
    "By evening the moment to name the real tension had passed.",
    "I answered the next request in four minutes to look responsive."
  ]

  function toggleRedaction(idx: number) {
    playPaper()
    setRedactions(prev =>
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx].sort()
    )
  }

  // Residue multi-select
  function toggleResidue(tag: ResidueTag) {
    setResidue(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag].slice(0, 4)
    )
  }

  // === RENDER ===
  return (
    <div className="min-h-screen bg-[#f6f2e9] text-[#1f1912] selection:bg-[#8b5e3c] selection:text-[#f6f2e9]">
      <div className="max-w-[1080px] mx-auto px-5 pb-28">
        {/* Civic header — always present, never loud */}
        <div className="flex items-center justify-between pt-9 pb-6 border-b border-[#c9a17a]/40">
          <div>
            <div className="mono text-[10px] tracking-[3.5px] text-[#6b5f4f] mb-px">CIVIC INSTRUMENT</div>
            <div className="text-[42px] leading-none tracking-[-2.2px] font-serif">remainder</div>
            <div className="text-[#6b5f4f] text-[13px] mt-0.5">the attention cost of constant optimization</div>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <button onClick={() => { setView('arcade'); playThunk() }} className="px-4 py-2 rounded-full border border-[#8a6f47] hover:bg-white/60 active:bg-white/80">arcade</button>
            <button onClick={() => { setView('replay'); loadCurrentReplay() }} className="px-4 py-2 rounded-full border border-[#8a6f47] hover:bg-white/60 active:bg-white/80 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> replay hall</button>
            <button onClick={() => { setView('ledger'); loadMyLedger() }} className="px-4 py-2 rounded-full border border-[#8a6f47] hover:bg-white/60 active:bg-white/80">my ledger</button>
            <button onClick={() => setShowAbout(true)} className="px-3 py-2 text-[#8b5e3c]"><Info className="w-4 h-4" /></button>
            <button onClick={() => { const nowMuted = toggleMute(); setMuted(nowMuted); toast(nowMuted ? 'Sound off' : 'Mechanical sounds on') }} className="px-3 py-2 text-[#8b5e3c]">
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Quiet presence when relevant */}
        {(view === 'replay' || view === 'arcade') && (
          <div className="flex items-center gap-2 text-xs text-[#6b5f4f] mt-3 mb-1">
            <Users className="w-3.5 h-3.5" />
            <span>{hallCount} people in the hall right now</span>
            <span className="text-[#c9a17a]">•</span>
            <span className="mono">unresolved questions are permitted to remain unresolved</span>
          </div>
        )}

        {/* ========== ARRIVAL — the threshold ========== */}
        {view === 'arrival' && (
          <div className="max-w-2xl pt-16 pb-12">
            <div className="text-[17px] leading-tight tracking-[-0.18px] text-[#3f3528]">
              You have arrived at a place that does not want to optimize you.
            </div>
            <div className="mt-6 text-[15px] text-[#4a3726] max-w-prose space-y-4">
              <p>Most of us are carrying a low, persistent remainder: the attention we withdrew from a hard conversation so that we could continue appearing competent, responsive, low-maintenance, or efficient. The unease that follows is real, but it has no sanctioned place in optimized life.</p>
              <p>This instrument will not help you have the conversation. It will not give you language, courage, or next steps. It offers a small, bounded ritual in which you can name the specific optimization, the overconfident bet you made at the time, the weather your attention was in, and the residue that actually remained.</p>
              <p>When you drop it, the remainder joins an anonymous, slowly re-woven public record — a cooperation replay — so that the pattern across many private edits becomes visible as a collective matter rather than a personal failure.</p>
            </div>

            <div className="mt-10 flex flex-col gap-3">
              <button
                onClick={() => { setView('arcade'); playThunk() }}
                className="w-full py-5 text-[17px] border-2 border-[#1f1912] hover:bg-[#1f1912] hover:text-[#f6f2e9] active:scale-[0.985] rounded-2xl flex items-center justify-center gap-3 transition-all"
              >
                Enter the Overconfidence Arcade <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => { setView('replay'); loadCurrentReplay() }} className="w-full py-4 text-[#8b5e3c] border border-[#c9a17a] rounded-2xl hover:bg-white/60">
                Go straight to the current replay
              </button>
            </div>

            <div className="mt-16 text-[11px] text-[#6b5f4f] max-w-[46ch]">
              Nothing here requires an account. Your words will never be attached to your name. The only thing you keep is the private receipt that lets you later ask for a specific deposit to be forgotten.
            </div>

            {/* Prominent links right on arrival */}
            <div className="mt-10 pt-6 border-t border-[#c9a17a]/40 text-sm flex flex-wrap gap-x-5 gap-y-1 text-[#8b5e3c]">
              <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" className="hover:underline flex items-center gap-1.5"><LinkIcon className="w-3.5 h-3.5" /> LinkedIn — mindful-abhay</a>
              <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" className="hover:underline flex items-center gap-1.5"><Heart className="w-3.5 h-3.5" /> Buy Abhay a coffee</a>
            </div>
          </div>
        )}

        {/* ========== THE OVERCONFIDENCE ARCADE ========== */}
        {view === 'arcade' && (
          <div className="pt-8">
            <div className="uppercase mono tracking-[2.5px] text-xs text-[#8b5e3c] mb-1">THE ARCADE</div>
            <div className="text-4xl tracking-[-1.6px] font-serif leading-none mb-2">Name the cost.<br />Place the bet.</div>
            <p className="max-w-prose text-[#4a3726]">Each machine asks you to make visible the small narrative edit you performed to keep the optimized story intact. Nothing is solved here.</p>

            {/* Machine 1 — Efficiency Confessor */}
            <div className="arcade mt-8 rounded-3xl p-7">
              <div className="mono text-[10px] tracking-[2px] text-[#8a6f47] mb-2">MACHINE ONE — THE EFFICIENCY CONFESSOR</div>
              <div className="text-xl tracking-tight mb-4">What optimization let the hard thing remain unaddressed?</div>
              <textarea
                value={optText}
                onChange={e => setOptText(e.target.value.slice(0, 240))}
                placeholder="I answered the 9:40pm message in six minutes so the team would not think I was slipping…"
                className="w-full h-24 bg-[#f8f5f0] border border-[#b39a73] rounded-2xl p-4 text-[15px] focus:outline-none resize-y"
              />
              <div className="mt-5">
                <div className="flex justify-between text-xs text-[#6b5f4f] mb-1.5">
                  <div>At the time, how confident were you that this cost nothing important?</div>
                  <div className="mono tabular-nums">{overconfidence}</div>
                </div>
                <input type="range" min={12} max={96} step={1} value={overconfidence} onChange={e => setOverconfidence(parseInt(e.target.value))} className="w-full" />
                <div className="text-[10px] text-[#8b5e3c] mt-1">High numbers mean you were very sure the edit was clean. The gap with what you actually felt later is the remainder.</div>
              </div>
            </div>

            {/* Machine 2 — Redaction Table */}
            <div className="arcade mt-5 rounded-3xl p-7">
              <div className="mono text-[10px] tracking-[2px] text-[#8a6f47] mb-2">MACHINE TWO — THE REDACTION TABLE</div>
              <div className="text-xl tracking-tight mb-3">Mark the sentences you routinely leave out of the story you tell.</div>
              <div className="cabinet-glass rounded-2xl p-5 space-y-2 text-[14.5px] leading-snug">
                {redactionScaffold.map((sentence, idx) => (
                  <button
                    key={idx}
                    onClick={() => toggleRedaction(idx)}
                    className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all ${redactions.includes(idx) ? 'bg-[#3f2a1f] text-[#f6f2e9] border-[#3f2a1f]' : 'border-[#b39a73] hover:border-[#8a6f47] bg-white'}`}
                  >
                    {redactions.includes(idx) ? '✕ ' : ''}{sentence}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-[#8b5e3c] mt-2">Each cut is a small withdrawal of attention from the relationship or the situation.</div>
            </div>

            {/* Machine 3 + 4 — Weather + Residue + Agency in one civic counter */}
            <div className="arcade mt-5 rounded-3xl p-7">
              <div className="mono text-[10px] tracking-[2px] text-[#8a6f47] mb-2">THE WEATHER GLASS + RESIDUE COUNTER + AGENCY DIAL</div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Weather */}
                <div>
                  <div className="text-sm mb-2 text-[#4a3726]">Current weather of attention</div>
                  <div className="flex flex-wrap gap-1.5">
                    {WEATHERS.map(w => (
                      <button key={w.id} onClick={() => { setWeather(w.id); playPaper() }} className={`px-3 py-1 rounded-full text-xs border ${weather === w.id ? 'bg-[#8b5e3c] text-[#f6f2e9] border-[#8b5e3c]' : 'border-[#c9a17a] hover:bg-white/70'}`}>
                        {w.label}
                      </button>
                    ))}
                  </div>
                  <input value={weatherNote} onChange={e => setWeatherNote(e.target.value.slice(0, 84))} placeholder="one breath note (optional)" className="mt-2 w-full bg-white border border-[#b39a73] rounded-xl px-3 py-1.5 text-sm" />
                </div>

                {/* Residue */}
                <div>
                  <div className="text-sm mb-2 text-[#4a3726]">Emotional residue left behind</div>
                  <div className="flex flex-wrap gap-1.5">
                    {RESIDUE_TAGS.map(tag => (
                      <button key={tag} onClick={() => toggleResidue(tag)} className={`residue-tag px-3 py-0.5 rounded-full text-xs ${residue.includes(tag) ? 'ring-1 ring-[#8b5e3c]' : ''}`}>
                        {tag.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs">
                    <span className="text-[#6b5f4f]">Intensity</span>
                    <input type="range" min={1} max={5} value={intensity} onChange={e => setIntensity(parseInt(e.target.value))} />
                    <span className="mono w-3 tabular-nums">{intensity}</span>
                  </div>
                </div>
              </div>

              {/* Agency */}
              <div className="mt-6">
                <div className="flex justify-between text-xs text-[#6b5f4f] mb-1">
                  <div>How much of this edit felt truly chosen versus structurally required?</div>
                  <div className="mono tabular-nums">{agency}</div>
                </div>
                <input type="range" min={0} max={100} value={agency} onChange={e => setAgency(parseInt(e.target.value))} className="w-full" />
                <div className="text-[10px] text-[#8b5e3c] mt-0.5">0 = almost no real discretion. 100 = I could have done otherwise without meaningful penalty.</div>
              </div>
            </div>

            {/* The Whisper Slot — explicitly unresolved */}
            <div className="mt-5 border border-[#8a6f47] rounded-3xl p-7 bg-[#f1ebdf]">
              <div className="mono text-[10px] tracking-[2px] text-[#8b5e3c] mb-2">THE WHISPER SLOT (OPTIONAL)</div>
              <div className="text-lg tracking-tight mb-2">The question I am still not asking.</div>
              <div className="text-xs text-[#8b5e3c] mb-3">This will not be solved, answered, or quoted. It may be witnessed only as part of what the commons is carrying.</div>
              <textarea value={unresolved} onChange={e => setUnresolved(e.target.value.slice(0, 220))} placeholder="I have not told them that the current arrangement quietly costs me sleep three nights a week…" className="whisper w-full h-20 bg-white/70 border border-[#c9a17a] rounded-2xl p-4 text-[15px] focus:outline-none" />
            </div>

            {/* The Drop */}
            <div className="mt-6">
              <button
                disabled={!canDrop || isDropping}
                onClick={dropRemainder}
                className="deposit-slot w-full py-5 rounded-3xl text-lg flex items-center justify-center gap-3 disabled:opacity-50 active:scale-[0.985] transition"
              >
                {isDropping ? 'PLACING INTO THE COMMONS…' : 'DROP THIS REMAINDER INTO THE COMMONS'}
                <Check className="w-5 h-5" />
              </button>
              <div className="text-center text-[10px] text-[#8b5e3c] mt-2 tracking-widest">ONE DEPOSIT PER SESSION WINDOW • YOU WILL RECEIVE A PRIVATE RECEIPT</div>
            </div>

            <div className="mt-4 text-xs text-[#6b5f4f]">After you drop, you will be taken to the replay hall where the pattern (including what you just placed) becomes visible on the next loom pass.</div>
          </div>
        )}

        {/* ========== THE REPLAY HALL — commons cooperation replay ========== */}
        {view === 'replay' && (
          <div className="pt-7">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="mono text-xs tracking-[2.5px] text-[#8b5e3c]">THE REPLAY HALL</div>
                <div className="text-3xl font-serif tracking-[-1.1px]">Commons Cooperation Replay</div>
              </div>
              <div className="flex gap-2">
                <button onClick={requestReweave} disabled={isWeaving} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#8a6f47] text-sm hover:bg-white/60 disabled:opacity-60">
                  <RotateCw className={`w-3.5 h-3.5 ${isWeaving ? 'animate-spin' : ''}`} /> {isWeaving ? 'WEAVING…' : 'REQUEST RE-WEAVE'}
                </button>
                <button onClick={exportWitnessCard} className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#8a6f47] text-sm hover:bg-white/60">
                  <Download className="w-3.5 h-3.5" /> WITNESS CARD
                </button>
              </div>
            </div>

            {/* The living visualization */}
            <div className="replay-canvas rounded-3xl p-3 md:p-4 mb-5">
              <ReplayCanvas snapshot={currentSnapshot || buildSnapshotFromPreview()} onSelectCluster={(k) => setSelectedCluster(k)} />
            </div>

            {/* The poetic civic reading — the actual output */}
            <div className="bg-[#f1ebdf] border border-[#b39a73] rounded-3xl p-7 mb-6">
              <div className="mono text-[10px] tracking-[2px] text-[#8b5e3c] mb-2">THE LOOM’S CURRENT READING</div>
              <div className="replay-summary space-y-3">
                {(currentSnapshot?.summary_lines || previewWeave.summary_lines).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-[#c9a17a]/50 text-xs text-[#6b5f4f] flex flex-wrap gap-x-5 gap-y-1">
                <span>Deposits in window: <span className="text-[#1f1912] tabular-nums">{(currentSnapshot || previewWeave).stats.depositCount}</span></span>
                <span>Mean agency: <span className="text-[#1f1912] tabular-nums">{(currentSnapshot || previewWeave).stats.meanAgency}</span></span>
                <span>Held questions visible to the loom: <span className="text-[#1f1912] tabular-nums">{(currentSnapshot || previewWeave).stats.heldQuestions}</span></span>
              </div>
            </div>

            {/* Witnessing / resonance */}
            <div className="text-sm text-[#4a3726] mb-2">Witnessing is how the pattern incorporates more of us. Select a knot in the canvas above and add a short resonance. It will be folded into the next weave without naming you or anyone else.</div>

            <AnimatePresence>
              {selectedCluster && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="border border-[#8a6f47] rounded-2xl p-5 bg-white mb-8">
                  <div className="text-xs text-[#8b5e3c] mb-1">RESONANCE FOR {selectedCluster.replace(/_/g, ' ')}</div>
                  <textarea value={resonanceText} onChange={e => setResonanceText(e.target.value.slice(0, 118))} placeholder="This knot lives in the evenings when I choose not to name the real cost." className="w-full h-16 border border-[#c9a17a] rounded-xl p-3 text-sm" />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setSelectedCluster(null)} className="flex-1 py-2 rounded-xl border">CANCEL</button>
                    <button onClick={addResonance} disabled={!resonanceText.trim()} className="flex-1 py-2 rounded-xl bg-[#1f1912] text-[#f6f2e9] disabled:opacity-50">ADD TO THE WEAVE</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="text-[11px] text-[#6b5f4f]">The replay updates when the loom runs again — either because enough new remainders have arrived or because someone (possibly you) asked it to pass. This is asynchronous group reflection by design.</div>
          </div>
        )}

        {/* ========== MY PRIVATE LEDGER ========== */}
        {view === 'ledger' && (
          <div className="pt-8 max-w-2xl">
            <div className="mono text-xs tracking-[2.5px] text-[#8b5e3c] mb-1">PRIVATE TO THIS BROWSER</div>
            <div className="text-3xl font-serif tracking-[-1px] mb-3">My Residue Ledger</div>
            <p className="text-[#4a3726] text-sm mb-6">Only you, holding the secret in this browser, can see these. You may request erasure of any deposit while its receipt is still valid.</p>

            {myDeposits.length === 0 && (
              <div className="text-[#6b5f4f] text-sm">You have not yet placed anything claimable in this session, or the worker could not be reached. Deposits you make while offline are kept locally until you erase them or close the browser.</div>
            )}

            <div className="space-y-3">
              {myDeposits.map((d, idx) => (
                <div key={idx} className="border border-[#c9a17a] rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3 bg-white/70">
                  <div className="flex-1 text-sm leading-snug">{d.summary || d.optimization_text?.slice(0, 92)}</div>
                  <button
                    disabled={!!isErasing}
                    onClick={() => eraseDeposit(d.id, d.receipt)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#8b5e3c] text-[#8b5e3c] flex items-center gap-1 hover:bg-[#f6f2e9] disabled:opacity-50"
                  >
                    <Trash2 className="w-3 h-3" /> {isErasing === d.id ? 'ERASING…' : 'ERASE THIS DEPOSIT'}
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-8 text-[10px] text-[#6b5f4f]">Erasing removes the deposit from future weaves and marks it tombstoned. Older weaves that already incorporated it are not retroactively altered — that is the nature of a public record.</div>
          </div>
        )}

        {/* Always-visible civic footer with the required prominent links */}
        <div className="fixed bottom-0 left-0 right-0 border-t border-[#c9a17a]/60 bg-[#f6f2e9]/95 backdrop-blur text-xs py-2.5">
          <div className="max-w-[1080px] mx-auto px-5 flex flex-col md:flex-row md:items-center gap-x-5 gap-y-0.5 text-[#6b5f4f]">
            <div>remainder is a public instrument for noticing, not for collecting.</div>
            <div className="flex-1" />
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" className="hover:text-[#8b5e3c] underline">LinkedIn — mindful-abhay</a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" className="hover:text-[#8b5e3c] underline">Buy Me a Coffee — abhaypratapsingh</a>
          </div>
        </div>
      </div>

      {/* About panel — rich, includes the full problem definition and links */}
      <AnimatePresence>
        {showAbout && (
          <div className="fixed inset-0 z-[90] bg-black/70 flex justify-end" onClick={() => setShowAbout(false)}>
            <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 30, opacity: 0 }} onClick={e => e.stopPropagation()} className="bg-[#f6f2e9] w-full max-w-lg h-full overflow-auto p-8 text-sm border-l border-[#8a6f47]">
              <button onClick={() => setShowAbout(false)} className="mb-8 text-[#8b5e3c]">Close</button>

              <div className="font-serif text-2xl tracking-tight mb-4">This is not a tool for having the conversation.</div>
              <div className="space-y-4 text-[#3f3528]">
                <p>It is a bounded, ceremonial place where you can name the attention you withdrew in order to remain optimized, and place that private cost into a public, slowly woven record so the pattern across many such withdrawals becomes visible.</p>
                <p>The output is never advice, never a script, never “here is how to say the hard thing.” The output is a cooperation replay: a partial, poetic account of what the commons is currently carrying because so many of us are performing similar narrative edits at the same time.</p>
              </div>

              <div className="mt-8 pt-6 border-t text-xs text-[#6b5f4f]">
                Built on Cloudflare (Pages, Workers, D1, Durable Objects, KV, R2, Workflows) entirely within the free tier. No accounts. No extraction. Unresolved questions are permitted to remain unresolved.
              </div>

              <div className="mt-8 space-y-2 text-sm">
                <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" className="flex items-center gap-2 text-[#8b5e3c] hover:underline"><LinkIcon className="w-4 h-4" /> Abhay on LinkedIn</a>
                <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" className="flex items-center gap-2 text-[#8b5e3c] hover:underline"><LinkIcon className="w-4 h-4" /> Buy Abhay a coffee</a>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// The actual interactive replay canvas — civic, slow, expressive
function ReplayCanvas({ snapshot, onSelectCluster }: { snapshot: ReplaySnapshot; onSelectCluster: (key: string) => void }) {
  const ref = useRef<HTMLCanvasElement>(null)
  const [hover, setHover] = useState<{ x: number; y: number; label?: string } | null>(null)

  useEffect(() => {
    const c = ref.current
    if (!c) return
    const ctx = c.getContext('2d', { alpha: true })!
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    c.width = 980 * dpr
    c.height = 520 * dpr
    c.style.width = '100%'
    c.style.height = 'auto'
    ctx.scale(dpr, dpr)

    const W = 980
    const H = 520

    ctx.clearRect(0, 0, W, H)

    // Paper grain
    ctx.fillStyle = '#f1ebdf'
    ctx.fillRect(0, 0, W, H)

    // Very subtle grid
    ctx.strokeStyle = '#d9c9a8'
    ctx.lineWidth = 0.6
    for (let x = 60; x < W; x += 72) {
      ctx.beginPath(); ctx.moveTo(x, 30); ctx.lineTo(x, H - 24); ctx.stroke()
    }
    for (let y = 48; y < H; y += 68) {
      ctx.beginPath(); ctx.moveTo(30, y); ctx.lineTo(W - 22, y); ctx.stroke()
    }

    const { threads, clusters, echoes } = snapshot.weave

    // Draw threads first (behind knots)
    ctx.strokeStyle = '#5c4630'
    ctx.lineWidth = 1.35
    ctx.lineJoin = 'round'
    for (const t of threads) {
      ctx.beginPath()
      const p = t.path
      ctx.moveTo(p[0][0] * W, p[0][1] * H)
      for (let i = 1; i < p.length; i++) {
        ctx.lineTo(p[i][0] * W, p[i][1] * H)
      }
      ctx.globalAlpha = 0.55 + (t.agency / 280)
      ctx.stroke()
    }
    ctx.globalAlpha = 1

    // Knots / clusters
    for (const cl of clusters) {
      const x = cl.x * W
      const y = cl.y * H
      const r = 8 + Math.min(19, cl.size * 2.6)

      // soft halo
      const grad = ctx.createRadialGradient(x, y, r * 0.4, x, y, r * 2.1)
      grad.addColorStop(0, 'rgba(63,42,31,0.12)')
      grad.addColorStop(1, 'rgba(63,42,31,0.0)')
      ctx.fillStyle = grad
      ctx.beginPath()
      ctx.arc(x, y, r * 2.1, 0, Math.PI * 2)
      ctx.fill()

      // knot
      ctx.fillStyle = '#3f2a1f'
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()

      // label
      ctx.fillStyle = '#4a3726'
      ctx.font = '500 11px ui-monospace, monospace'
      ctx.fillText(cl.label.slice(0, 32), x - r - 4, y - r - 7)
    }

    // Echo dots (resonances that have been folded in)
    ctx.fillStyle = '#8b5e3c'
    for (const e of echoes) {
      const cl = clusters.find(c => c.key === e.cluster_key)
      if (cl) {
        const ex = cl.x * W + (Math.sin(e.text.length) * 11)
        const ey = cl.y * H + 13
        ctx.beginPath()
        ctx.arc(ex, ey, 2.6, 0, Math.PI * 2)
        ctx.fill()
      }
    }
  }, [snapshot])

  function onMove(e: React.MouseEvent) {
    const rect = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 980
    const y = ((e.clientY - rect.top) / rect.height) * 520

    let found: string | null = null
    for (const cl of snapshot.weave.clusters) {
      const dx = (cl.x * 980) - x
      const dy = (cl.y * 520) - y
      if (dx * dx + dy * dy < (18 + cl.size * 3.5) ** 2) {
        found = cl.key
        break
      }
    }
    setHover(found ? { x: e.clientX, y: e.clientY, label: snapshot.weave.clusters.find(c => c.key === found)?.label } : null)
    if (found && (e.buttons === 1 || e.type === 'click')) {
      onSelectCluster(found)
    }
  }

  return (
    <div className="relative">
      <canvas
        ref={ref}
        onMouseMove={onMove}
        onClick={onMove}
        className="w-full rounded-2xl cursor-crosshair"
        style={{ maxHeight: 520 }}
      />
      {hover && hover.label && (
        <div className="absolute pointer-events-none text-xs bg-white/90 border border-[#c9a17a] px-2 py-0.5 rounded" style={{ left: hover.x + 12, top: hover.y + 8 }}>
          {hover.label} — click to add resonance
        </div>
      )}
      <div className="absolute bottom-3 right-4 text-[10px] text-[#6b5f4f] mono pointer-events-none">click a knot to witness</div>
    </div>
  )
}

// Seed a few beautiful demo remainders so the replay hall is meaningful on first load
function seedDemoDeposits(): Deposit[] {
  const now = Math.floor(Date.now() / 1000)
  return [
    { id: 'd1', created_at: now - 4000, optimization_text: 'I replied to the late ping with a crisp “on it” instead of calling to say the scope had already slipped.', overconfidence: 81, weather_id: 'brittle_clear', weather_note: null, residue_tags: ['performative_lightness', 'hollowness'], residue_intensity: 3, agency_rating: 22, unresolved_text: null },
    { id: 'd2', created_at: now - 3100, optimization_text: 'Told my partner “let’s talk about it this weekend” for the third week running while the real conversation sat in my chest.', overconfidence: 64, weather_id: 'held_breath', weather_note: 'I keep choosing the version of me that is not difficult.', residue_tags: ['tender_avoidance', 'quiet_dread'], residue_intensity: 4, agency_rating: 37, unresolved_text: 'I have not said that the current split is quietly breaking something in me.' },
    { id: 'd3', created_at: now - 1900, optimization_text: 'Chose the fast path on the team decision so I would still look like the person who “makes things easy to work with.”', overconfidence: 73, weather_id: 'low_static', weather_note: null, residue_tags: ['numb_competence'], residue_intensity: 2, agency_rating: 48, unresolved_text: null },
  ]
}
